import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";

const defaultForm = {
  nom: "",
  email: "",
  mot_de_passe: "",
  ville: "",
  role: "directeur",
};

const roleLabel = {
  directeur: "Directeur",
  admin_fete: "Admin Fête",
  admin_oumrah: "Admin Oumrah",
  admin_sacrifices: "Admin Sacrifices",
  delegue_sacrifices: "Délégué Sacrifices",
};
const roleColor = {
  directeur: "#1A73E8",
  admin_fete: "#43a047",
  admin_oumrah: "#fb8c00",
  admin_sacrifices: "#7b1fa2",
  delegue_sacrifices: "#8e24aa",
};

function Utilisateurs() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchUsers = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpen = (user = null) => {
    setError("");
    if (user) {
      setForm({
        nom: user.nom,
        email: user.email,
        ville: user.ville || "",
        mot_de_passe: "",
        role: user.role || "directeur",
      });
      setEditId(user.id);
    } else {
      setForm(defaultForm);
      setEditId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm(defaultForm);
    setEditId(null);
    setError("");
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      if (editId) {
        await api.put("/users/" + editId, form);
      } else {
        await api.post("/users", form);
      }
      fetchUsers();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur serveur");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer cet utilisateur ?")) {
      try {
        await api.delete("/users/" + id);
        fetchUsers();
      } catch (err) {
        alert(err.response?.data?.message || "Erreur serveur");
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Utilisateurs
                </MDTypography>
                <MDButton variant="outlined" color="white" size="small" onClick={() => handleOpen()}>
                  <Icon>add</Icon>&nbsp;Ajouter
                </MDButton>
              </MDBox>
              <MDBox pt={3} px={3} pb={3}>
                <Grid container spacing={3}>
                  {users.map((user) => (
                    <Grid item xs={12} md={6} lg={4} key={user.id}>
                      <Card sx={{ p: 2, border: "1px solid #eee" }}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <MDTypography variant="h6">{user.nom}</MDTypography>
                          <MDBox display="flex" alignItems="center">
                            <Icon sx={{ cursor: "pointer", mr: 1, color: "info.main" }} onClick={() => handleOpen(user)}>
                              edit
                            </Icon>
                            <Icon sx={{ cursor: "pointer", color: "error.main" }} onClick={() => handleDelete(user.id)}>
                              cancel
                            </Icon>
                          </MDBox>
                        </MDBox>
                        <Chip
                          label={roleLabel[user.role] || user.role}
                          size="small"
                          sx={{ bgcolor: roleColor[user.role] || "#9e9e9e", color: "white", fontWeight: "bold", mb: 1 }}
                        />
                        <MDTypography variant="caption" color="text" display="block">
                          Email : {user.email}
                        </MDTypography>
                        {user.ville && (
                          <MDTypography variant="caption" color="text" display="block">
                            Ville : {user.ville}
                          </MDTypography>
                        )}
                        <MDTypography variant="caption" color="text" display="block">
                          Créé le : {new Date(user.created_at).toLocaleDateString("fr-FR")}
                        </MDTypography>
                      </Card>
                    </Grid>
                  ))}
                  {users.length === 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="body2" color="text" textAlign="center">
                        Aucun utilisateur. Cliquez sur "Ajouter".
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Modifier l'utilisateur" : "Ajouter un utilisateur"}</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="role-label">Rôle</InputLabel>
              <Select
                labelId="role-label" label="Rôle" sx={{ height: 44 }}
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <MenuItem value="directeur">Directeur de ville</MenuItem>
                <MenuItem value="admin_fete">Admin Fête</MenuItem>
                <MenuItem value="admin_oumrah">Admin Oumrah</MenuItem>
                <MenuItem value="admin_sacrifices">Admin Sacrifices</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Nom complet" fullWidth
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
            />
            <TextField
              label="Email" fullWidth type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <TextField
              label={editId ? "Nouveau mot de passe (laisser vide pour ne pas changer)" : "Mot de passe"}
              fullWidth type="password"
              value={form.mot_de_passe}
              onChange={(e) => setForm({ ...form, mot_de_passe: e.target.value })}
            />
            <TextField
              label="Ville (pour les directeurs)" fullWidth
              value={form.ville}
              onChange={(e) => setForm({ ...form, ville: e.target.value })}
            />
            {error && (
              <MDTypography variant="caption" color="error">{error}</MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose} color="secondary">Annuler</MDButton>
          <MDButton onClick={handleSubmit} color="info" disabled={loading || !form.nom || !form.email || (!editId && !form.mot_de_passe)}>
            {loading ? "Enregistrement..." : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Utilisateurs;
